import axios from 'axios';

const backendHost = process.env.REACT_APP_BACKEND_HOST;
if (!backendHost) {
  throw new Error('REACT_APP_BACKEND_HOST environment variable is not defined');
}

const axiosClient = axios.create({
  baseURL: backendHost + '/api',
  withCredentials: true,
});

// Centralized response interceptor for handling 401 errors
axiosClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Set a flag in local storage
      localStorage.setItem('showSessionExpired', 'true');
      window.location.href = '/login'; // Redirect to login
    }
    return Promise.reject(error);
  },
);

// Utility to check if an error is an AxiosError
interface AxiosError {
  response?: {
    status?: number;
    data: string | { title?: string; message?: string };
  };
}

const isAxiosError = (error: unknown): error is AxiosError => {
  return (error as AxiosError).response !== undefined;
};

// GET request handler
export const get = async (url: string, config?: any) => {
  try {
    const response = await axiosClient.get(url, config);
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      if (typeof error.response?.data === 'string') throw new Error(error.response?.data);
      if (typeof error.response?.data?.title === 'string') throw new Error(error.response?.data.title);
      if (typeof error.response?.data?.message === 'string') throw new Error(error.response?.data.message);
    }
    throw error;
  }
};

// POST request handler
export const post = async (url: string, data: any, config?: any) => {
  try {
    const response = await axiosClient.post(url, data, config);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (typeof error.response?.data === 'string') throw new Error(error.response?.data);
      if (typeof error.response?.data?.title === 'string') throw new Error(error.response?.data.title);
      if (typeof error.response?.data?.message === 'string') throw new Error(error.response?.data.message);
    }
    throw error;
  }
};

// POST returning the full response, for the rare callers that need response
// headers (e.g. the PDF export reads its X-Pdf-* size breakdown).
export const postRaw = async (url: string, data: any, config?: any) => {
  try {
    return await axiosClient.post(url, data, config);
  } catch (error) {
    if (isAxiosError(error)) {
      if (typeof error.response?.data === 'string') throw new Error(error.response?.data);
      if (typeof error.response?.data?.title === 'string') throw new Error(error.response?.data.title);
      if (typeof error.response?.data?.message === 'string') throw new Error(error.response?.data.message);
    }
    throw error;
  }
};

// PUT request handler
export const put = async (url: string, data: any, config?: any) => {
  try {
    const response = await axiosClient.put(url, data, config);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (typeof error.response?.data === 'string') throw new Error(error.response?.data);
      if (typeof error.response?.data?.title === 'string') throw new Error(error.response?.data.title);
      if (typeof error.response?.data?.message === 'string') throw new Error(error.response?.data.message);
    }
    throw error;
  }
};

// PATCH request handler
export const patch = async (url: string, data: any, config?: any) => {
  try {
    const response = await axiosClient.patch(url, data, config);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (typeof error.response?.data === 'string') throw new Error(error.response?.data);
      if (typeof error.response?.data?.title === 'string') throw new Error(error.response?.data.title);
      if (typeof error.response?.data?.message === 'string') throw new Error(error.response?.data.message);
    }
    throw error;
  }
};

// DELETE request handler
export const del = async (url: string, config?: any) => {
  try {
    const response = await axiosClient.delete(url, config);
    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      if (typeof error.response?.data === 'string') throw new Error(error.response?.data);
      if (typeof error.response?.data?.title === 'string') throw new Error(error.response?.data.title);
      if (typeof error.response?.data?.message === 'string') throw new Error(error.response?.data.message);
    }
    throw error;
  }
};

export default axiosClient;
